package dev.kotlinfest._2026.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.*
import dev.kotlinfest._2026.components.layouts.PageLayoutData
import dev.kotlinfest._2026.components.widgets.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element

private data class NewsItem(val date: String, val text: String, val link: String? = null)

val HeroStyle = CssStyle {
    base {
        Modifier.sitePageHero(824)
    }
    Breakpoint.MD {
        Modifier.sitePageHero(932)
    }
}

val HeroMainStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().padding(leftRight = SiteLayout.MobileMarginPx.px).styleModifier {
            property("position", "absolute")
            property("top", "103px")
            property("left", "50%")
            property("transform", "translateX(-50%)")
        }
    }
    Breakpoint.MD { Modifier.padding(leftRight = 0.px).styleModifier { property("top", "160px") } }
}

val HeroLogoStyle = CssStyle {
    base { Modifier.width(240.px).height(198.px) }
    Breakpoint.MD { Modifier.width(400.px).height(330.px) }
}

val EventDateStyle = CssStyle {
    base { Modifier.fontFamily(LatinFontFamily, "sans-serif").fontSize(32.px).fontWeight(600).lineHeight(1.6).letterSpacing(0.08.em) }
    Breakpoint.MD { Modifier.fontSize(48.px).lineHeight(1.2) }
}

val VenueStyle = CssStyle {
    base { Modifier.fontSize(16.px).fontWeight(700).lineHeight(1.6).letterSpacing(0.1.em) }
    Breakpoint.MD { Modifier.fontSize(36.px).letterSpacing(0.08.em) }
}

val HeroButtonGridStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gridTemplateColumns { size(1.fr) }.gap(16.px) }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } }.gap(20.px) }
}

val FollowStyle = CssStyle {
    base { Modifier.fillMaxWidth().margin(top = 48.px).gap(12.px) }
    Breakpoint.MD {
        Modifier.width(364.px).margin(top = 0.px).padding(32.px).styleModifier {
            property("position", "absolute")
            property("right", "max(0px, calc((100vw - ${SiteLayout.DesktopDesignWidthPx}px) / 2))")
            property("top", "534px")
        }
    }
}

val FollowCopyStyle = CssStyle {
    base { Modifier.width(300.px).maxWidth(100.percent).gap(2.px) }
}

val FollowTitleFrameStyle = CssStyle {
    base {
        Modifier.width(112.px).height(20.px).margin(bottom = 8.px).styleModifier {
            property("overflow", "hidden")
            property("position", "relative")
        }
    }
}

val FollowTitleImageStyle = CssStyle {
    base {
        Modifier.width(300.px).height(32.px).styleModifier {
            property("left", "-94px")
            property("position", "absolute")
            property("top", "-6px")
        }
    }
}

val ConceptStyle = CssStyle {
    base {
        Modifier.fillMaxWidth()
            .padding(top = 64.px, bottom = 64.px, leftRight = SiteLayout.MobileMarginPx.px)
            .gap(48.px)
    }
    Breakpoint.MD {
        Modifier.padding(top = 120.px, bottom = 120.px).gap(64.px).desktopPageHorizontalPadding()
    }
}

val ConceptContentStyle = CssStyle {
    base { Modifier.gap(48.px) }
    Breakpoint.MD { Modifier.gap(64.px) }
}

val ConceptHeadingStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(24.px) }
    Breakpoint.MD { Modifier.gap(16.px) }
}

val ConceptTitleStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().display(DisplayStyle.Block)
    }
    Breakpoint.MD {
        Modifier.width(73.3.percent).maxWidth(938.px).display(DisplayStyle.Block).styleModifier {
            property("margin-bottom", "clamp(24px, 3.125vw, 40px)")
        }
    }
}

val ConceptLeadStyle = CssStyle {
    base { Modifier.fontSize(22.px).fontWeight(700).lineHeight(1.6).letterSpacing(0.08.em) }
    Breakpoint.MD { Modifier.fontSize(26.px).lineHeight(2.0) }
}

val NewsSectionStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(16.px) }
    Breakpoint.MD { Modifier.gap(12.px) }
}

val NewsListStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(0.px)
            .borderTop(1.px, LineStyle.Solid, SitePalette.brand.border)
    }
}

val NewsRowStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().display(DisplayStyle.Grid)
            .padding(top = 14.px, right = 0.px, bottom = 14.px, left = 16.px)
            .borderBottom(1.px, LineStyle.Solid, SitePalette.brand.border)
            .styleModifier {
                property("grid-template-columns", "minmax(0, 1fr)")
                property("row-gap", "4px")
                property("align-items", "center")
                property("position", "relative")
            }
    }
    Breakpoint.MD {
        Modifier.padding(top = 12.px, right = 0.px, bottom = 12.px, left = 16.px).styleModifier {
            property("grid-template-columns", "120px minmax(0, 1fr)")
            property("column-gap", "24px")
            property("row-gap", "0")
        }
    }
}

val NewsDateStyle = CssStyle {
    base { Modifier.styleModifier { property("grid-column", "1 / -1") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-column", "auto") } }
}

val NewsEntryAccentStyle = CssStyle {
    base {
        Modifier.color(SitePalette.brand.ink).styleModifier {
            property("--news-accent-inset", "6px")
            property("--news-accent-rest-scale", "0.82")
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("--news-accent-inset", "8px")
            property("--news-accent-rest-scale", "0.6")
        }
    }
    cssRule("::after") {
        Modifier.width(3.px).styleModifier {
            property("content", "\"\"")
            property("position", "absolute")
            property("left", "0")
            property("top", "var(--news-accent-inset)")
            property("bottom", "var(--news-accent-inset)")
            property("border-radius", "999px")
            property("background", "linear-gradient(180deg, #7f52ff 0%, #c811e2 55%, #e54857 100%)")
            property("opacity", "0.7")
            property("transform", "scaleY(var(--news-accent-rest-scale))")
            property("transform-origin", "center")
            property("transition", "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease")
        }
    }
}

val NewsEntryInteractiveStyle = CssStyle {
    cssRule(":hover::after") {
        Modifier.opacity(1).styleModifier { property("transform", "scaleY(1)") }
    }
    cssRule(":focus-visible::after") {
        Modifier.opacity(1).styleModifier { property("transform", "scaleY(1)") }
    }
    cssRule(":focus-visible") {
        Modifier.styleModifier {
            property("outline", "2px solid #c811e2")
            property("outline-offset", "4px")
        }
    }
}

@Composable
private fun NewsRowContent(item: NewsItem) {
    SpanText(item.date, EnMetadataStyle.toModifier().then(NewsDateStyle.toModifier()))
    SpanText(item.text, JpDetailTextStyle.toModifier())
}

val PhotoGridStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { size(1.fr) }.gap(16.px) }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } }.gap(20.px) }
}

val OverviewStyle = CssStyle {
    base {
        Modifier.fillMaxWidth()
            .padding(top = 64.px, bottom = 64.px, leftRight = SiteLayout.MobileMarginPx.px)
            .backgroundColor(SitePalette.brand.softPink)
            .fixedPageBackground()
    }
    Breakpoint.MD {
        Modifier.padding(top = 120.px, bottom = 159.px)
            .desktopPageHorizontalPadding(minimumPx = SiteLayout.MobileMarginPx)
            .fixedPageBackground()
    }
}

val OverviewCardStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(24.px).gap(24.px).backgroundColor(SitePalette.brand.white).borderRadius(20.px) }
    Breakpoint.MD {
        Modifier.padding(topBottom = 40.px)
            .desktopPageHorizontalPadding(minimumPx = SiteLayout.MobileMarginPx)
    }
}

val OverviewRowStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { size(1.fr) }.gap(8.px) }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("grid-template-columns", "clamp(80px, 8.33vw, 120px) minmax(0, 1fr) auto")
            property("gap", "clamp(12px, 1.67vw, 24px)")
            property("align-items", "center")
        }
    }
}

val SponsorGridStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gap(16.px).styleModifier { property("justify-items", "center") } }
    Breakpoint.MD { Modifier.gap(20.px) }
}

// Keep the pamphlet's 2 / 3 / 5 columns when space permits.
// On narrow screens, reduce columns to keep logos above their minimum sizes.
val KotoriSponsorGridStyle = CssStyle {
    base { Modifier.gridTemplateColumns { size(1.fr) } }
    Breakpoint.SM { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
}
val GoldSponsorGridStyle = CssStyle {
    base { Modifier.gridTemplateColumns { size(1.fr) } }
    Breakpoint.SM { Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } } }
}
val SilverSponsorGridStyle = CssStyle {
    base { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
    Breakpoint.SM { Modifier.gridTemplateColumns { repeat(5) { size(1.fr) } } }
}

val SponsorCardStyle = CssStyle {
    base { Modifier.fillMaxWidth().minWidth(0.px).gap(12.px) }
}
val SponsorLogoBoxStyle = CssStyle {
    // Cap the logo area independently, so company names can use the full grid cell.
    base { Modifier.fillMaxWidth().maxWidth(413.34.px) }
}
val GoldSponsorLogoBoxStyle = CssStyle {
    // A 160px logo area, compared with up to 314px for Kotori.
    base { Modifier.maxWidth(210.53.px) }
}

val SilverSponsorLogoBoxStyle = CssStyle {
    // A 100px logo area keeps the three sponsorship tiers visually distinct.
    base { Modifier.maxWidth(131.58.px) }
}

val SponsorsStyle = CssStyle {
    base {
        Modifier.fillMaxWidth()
            .padding(top = 64.px, bottom = 64.px, leftRight = SiteLayout.MobileMarginPx.px)
    }
    Breakpoint.MD {
        Modifier.desktopPageHorizontalPadding().styleModifier {
            property("padding-top", "clamp(64px, 8.33vw, 120px)")
            property("padding-bottom", "clamp(64px, 8.33vw, 120px)")
        }
    }
}

val SponsorsContentStyle = CssStyle {
    base { Modifier.gap(48.px) }
    Breakpoint.MD {
        Modifier.styleModifier { property("gap", "clamp(48px, 4.44vw, 64px)") }
    }
}

val HomeSponsorsStyle = CssStyle {
    base {
        Modifier.fillMaxWidth()
            .padding(top = 40.px, bottom = 40.px, leftRight = SiteLayout.MobileMarginPx.px)
    }
    Breakpoint.MD {
        Modifier.padding(topBottom = 64.px).desktopPageHorizontalPadding()
    }
}

val HomeSponsorsContentStyle = CssStyle {
    base { Modifier.gap(32.px) }
}

val SponsorIntroStyle = CssStyle {
    base { Modifier.fontSize(16.px).fontWeight(700).lineHeight(2.0).letterSpacing(0.08.em) }
    Breakpoint.MD {
        Modifier.styleModifier { property("font-size", "clamp(16px, 1.81vw, 26px)") }
    }
}

val KotoriSponsorNameStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().fontSize(16.px).fontWeight(700).lineHeight(1.6).styleModifier {
            property("letter-spacing", "0.1em")
            property("overflow-wrap", "anywhere")
            property("text-align", "center")
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("font-size", "clamp(10px, 1.11vw, 16px)")
            property("letter-spacing", "clamp(0.04em, 0.4vw, 0.1em)")
        }
    }
}

val CompactSponsorNameStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().fontSize(14.px).fontWeight(700).lineHeight(1.6).styleModifier {
            property("letter-spacing", "0.1em")
            property("text-align", "center")
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("font-size", "clamp(10px, 1.11vw, 16px)")
            property("letter-spacing", "clamp(0.04em, 0.4vw, 0.1em)")
            property("overflow-wrap", "anywhere")
        }
    }
}

@Composable
private fun FollowUs(modifier: Modifier = Modifier) {
    Column(FollowStyle.toModifier().then(modifier), horizontalAlignment = Alignment.CenterHorizontally) {
        Column(FollowCopyStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            Box(FollowTitleFrameStyle.toModifier()) {
                Image(
                    "/svg/follow-us.svg",
                    "Follow us!",
                    FollowTitleImageStyle.toModifier(),
                )
            }
            Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                SpanText(
                    "チケット販売中",
                    JpDetailTextStyle.toModifier().styleModifier { property("white-space", "nowrap") },
                )
                SpanText(
                    "公式Xでも情報を発信中！",
                    JpDetailTextStyle.toModifier().styleModifier { property("white-space", "nowrap") },
                )
            }
        }
        SiteButton("@kotlin_fest", SiteLinks.X, type = SiteButtonType.SECONDARY, language = SiteButtonLanguage.EN)
    }
}

@Composable
private fun Hero() {
    var motionReady by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        // Export the waiting state so the HTML snapshot cannot play the entrance
        // animation before Compose replaces it with the live page.
        if (!AppGlobals.isExporting) motionReady = true
    }

    Box(HeroStyle.toModifier().then(if (motionReady) Modifier.classNames("hero-motion-ready") else Modifier)) {
        Column(HeroMainStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            H1(Modifier.margin(0.px).toAttrs()) {
                Box(HeroLogoStyle.toModifier().classNames("hero-logo-motion")) {
                    Image("/svg/Kotlinfest2026_logo-data_1-1.svg", "Kotlin Fest 2026", Modifier.fillMaxSize())
                }
            }
            Column(Modifier.fillMaxWidth().gap(24.px), horizontalAlignment = Alignment.CenterHorizontally) {
                Column(
                    Modifier.fillMaxWidth().classNames("hero-fade-up-motion")
                        .styleModifier { property("--motion-delay", "650ms") },
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    SpanText("2026.11.14", EventDateStyle.toModifier())
                    SpanText("＠東京コンファレンスセンター品川", VenueStyle.toModifier())
                }
                Row(
                    HeroButtonGridStyle.toModifier().classNames("hero-fade-up-motion")
                        .styleModifier { property("--motion-delay", "820ms") },
                ) {
                    SiteButton("タイムテーブル", "/timetable", size = SiteButtonSize.LARGE)
                    SiteButton("チケットを購入", "/ticket", size = SiteButtonSize.LARGE)
                }
            }
            FollowUs(
                Modifier.classNames("hero-fade-up-motion")
                    .styleModifier { property("--motion-delay", "1000ms") },
            )
        }
    }
}

private val news = listOf(
    NewsItem("2026.10.28", "託児サービスのご案内", "/childcare"),
    NewsItem("2026.09.06", "タイムテーブルを公開しました", "/timetable"),
    NewsItem("2026.08.24", "チケットの販売を開始しました", "/ticket"),
    NewsItem("2026.07.15", "当日スタッフの募集を開始しました", SiteLinks.StaffRecruitment),
    NewsItem("2026.07.01", "スカラーシップ制度の募集を開始しました", "/scholarship"),
    NewsItem("2026.06.09", "ご協賛のお願い", SiteLinks.Sponsorship),
    NewsItem("2026.06.08", "セッション募集を開始しました", SiteLinks.SessionProposal),
    NewsItem("2026.06.04", "Kotlin Fest 2026 のサイトを公開しました"),
)

@Composable
private fun Concept() {
    Column(ConceptStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
        Column(ContentContainerStyle.toModifier().then(ConceptContentStyle.toModifier())) {
            Column(ConceptHeadingStyle.toModifier()) {
                Box(
                    Modifier.fillMaxWidth().classNames("concept-reveal-trigger")
                        .attr("data-reveal", "concept-title"),
                ) {
                    H2(Modifier.fillMaxWidth().margin(0.px).classNames("concept-pop-reveal").toAttrs()) {
                        Image(
                            "/svg/kotlin-with-love.svg",
                            "KOTLIN, WITH LOVE!",
                            ConceptTitleStyle.toModifier(),
                        )
                    }
                }
                SpanText(
                    "Kotlin Festは「Kotlinを愛でる」をテーマにした技術カンファレンスです。\n2018年に第1回を開催し、今年で6回目の開催となります。",
                    ConceptLeadStyle.toModifier().styleModifier { property("white-space", "pre-line") },
                )
            }
            Column(NewsSectionStyle.toModifier()) {
                H2(AccentEnSectionTitleStyle.toModifier().margin(0.px).toAttrs()) { Text("News") }
                Column(NewsListStyle.toModifier()) {
                    news.forEach { item ->
                        if (item.link == null) {
                            Row(
                                NewsRowStyle.toModifier()
                                    .then(NewsEntryAccentStyle.toModifier())
                                    .classNames("news-entry-accent"),
                            ) {
                                NewsRowContent(item)
                            }
                        } else {
                            Link(
                                item.link,
                                NewsRowStyle.toModifier()
                                    .then(NewsEntryAccentStyle.toModifier())
                                    .then(NewsEntryInteractiveStyle.toModifier())
                                    .classNames("news-entry-accent", "news-entry-link")
                                    .styleModifier { property("cursor", "pointer") },
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            ) {
                                NewsRowContent(item)
                            }
                        }
                    }
                }
            }
            Row(PhotoGridStyle.toModifier()) {
                listOf("event-2025-01.jpg", "event-2025-03.jpg", "event-2025-speaker.jpg").forEachIndexed { index, name ->
                    Image(
                        "/img/$name",
                        "Kotlin Fest 会場の様子 ${index + 1}",
                        Modifier.fillMaxWidth().aspectRatio(1.5).borderRadius(10.px)
                            .styleModifier { property("object-fit", "cover") },
                    )
                }
            }
        }
    }
}

@Composable
private fun OverviewRow(
    label: String,
    lines: List<String>,
    buttonLabel: String,
    buttonHref: String? = null,
    last: Boolean = false,
    venueLink: String? = null,
) {
    Row(
        OverviewRowStyle.toModifier().then(
            if (last) Modifier else Modifier.padding(bottom = 24.px).borderBottom(1.px, LineStyle.Solid, SitePalette.brand.pink),
        ),
    ) {
        SpanText(
            label,
            JpDetailTextStyle.toModifier().fillMaxWidth().color(SitePalette.brand.pink),
        )
        Column(JpDetailTextStyle.toModifier().gap(0.px)) {
            lines.forEachIndexed { index, line ->
                if (venueLink != null && index == 0) {
                    Link(venueLink, line, variant = UncoloredLinkVariant)
                } else {
                    SpanText(line)
                }
            }
        }
        Box(Modifier.styleModifier { property("justify-self", "start") }) {
            SiteButton(buttonLabel, href = buttonHref, enabled = buttonHref != null)
        }
    }
}

@Composable
private fun Overview() {
    Column(OverviewStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
        Column(ContentContainerStyle.toModifier().gap(12.px)) {
            H2(EnSectionTitleStyle.toModifier().margin(0.px).toAttrs()) { Text("Overview") }
            Column(OverviewCardStyle.toModifier()) {
                OverviewRow(
                    "開催日",
                    listOf("2026年11月14日（土）", "タイムテーブル公開中"),
                    "タイムテーブル",
                    buttonHref = "/timetable",
                )
                OverviewRow(
                    "会場",
                    listOf("東京コンファレンスセンター品川", "〒108-0075 東京都港区港南1丁目9-36 アレア品川 3F-5F"),
                    "地図",
                    buttonHref = SiteLinks.GoogleMap,
                    venueLink = SiteLinks.Venue,
                )
                OverviewRow(
                    "チケット",
                    listOf("販売中"),
                    "詳細・購入",
                    buttonHref = "/ticket",
                    last = true,
                )
            }
        }
    }
}

@Composable
private fun SponsorPlanBlock(plan: SponsorPlan, showCompanyNames: Boolean) {
    val gridStyle = when (plan.columns) {
        2 -> KotoriSponsorGridStyle
        3 -> GoldSponsorGridStyle
        else -> SilverSponsorGridStyle
    }
    Column(Modifier.fillMaxWidth().gap(12.px)) {
        H2(AccentEnSectionTitleStyle.toModifier().margin(0.px).toAttrs()) { Text(plan.title) }
        Row(
            SponsorGridStyle.toModifier().then(gridStyle.toModifier()).then(
                if (showCompanyNames) {
                    Modifier
                } else {
                    Modifier.maxWidth(1040.px).styleModifier {
                        property("row-gap", "8px")
                        property("margin-inline", "auto")
                    }
                },
            ),
        ) {
            plan.sponsors.forEach { sponsor ->
                Column(
                    SponsorCardStyle.toModifier(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Box(
                        SponsorLogoBoxStyle.toModifier().then(
                            when (plan.columns) {
                                3 -> GoldSponsorLogoBoxStyle.toModifier()
                                5 -> SilverSponsorLogoBoxStyle.toModifier()
                                else -> Modifier
                            },
                        ).aspectRatio(1.5).backgroundColor(SitePalette.brand.white),
                        contentAlignment = Alignment.Center,
                    ) {
                        // White isolation area surrounds the original, unmodified artwork.
                        val logo: @Composable () -> Unit = {
                            Image(
                                "/img/sponsors/${sponsor.logo}",
                                sponsor.name,
                                Modifier.width(sponsor.widthPercent.percent).height(sponsor.heightPercent.percent)
                                    .styleModifier { property("object-fit", "contain") },
                            )
                        }
                        if (sponsor.url != null) {
                            Link(
                                sponsor.url,
                                modifier = Modifier.fillMaxSize().display(DisplayStyle.Flex)
                                    .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center),
                                variant = UndecoratedLinkVariant,
                            ) {
                                logo()
                            }
                        } else {
                            logo()
                        }
                    }
                    if (showCompanyNames) {
                        SpanText(
                            sponsor.name,
                            (if (plan.columns == 2) KotoriSponsorNameStyle else CompactSponsorNameStyle).toModifier(),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RevealOnScrollEffect() {
    DisposableEffect(Unit) {
        val revealElements = document.querySelectorAll("[data-reveal]")
            .let { nodes ->
                buildList {
                    for (index in 0 until nodes.length) {
                        (nodes.item(index) as? Element)?.let(::add)
                    }
                }
            }
        val reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        val observers: List<IntersectionObserver> = if (reducedMotion) {
            revealElements.forEach { it.classList.add("is-revealed") }
            emptyList()
        } else {
            document.documentElement?.classList?.add("motion-ready")
            runCatching {
                val conceptTitles = revealElements.filter {
                    it.getAttribute("data-reveal") == "concept-title"
                }
                val standardElements = revealElements - conceptTitles.toSet()
                buildList {
                    if (standardElements.isNotEmpty()) {
                        add(
                            IntersectionObserver(
                                options = IntersectionObserver.Options(
                                    rootMargin = "0px 0px -10% 0px",
                                    thresholds = listOf(0.15),
                                ),
                            ) { entries, activeObserver ->
                                entries.filter { it.isIntersecting }.forEach { entry ->
                                    entry.target.classList.add("is-revealed")
                                    activeObserver.unobserve(entry.target)
                                }
                            }.also { activeObserver ->
                                standardElements.forEach(activeObserver::observe)
                            },
                        )
                    }

                    if (conceptTitles.isNotEmpty()) {
                        add(
                            IntersectionObserver(
                                options = IntersectionObserver.Options(thresholds = listOf(1.0)),
                            ) { entries, activeObserver ->
                                entries
                                    .filter { it.isIntersecting && it.intersectionRatio >= 0.999 }
                                    .forEach { entry ->
                                        entry.target.classList.add("is-revealed")
                                        activeObserver.unobserve(entry.target)
                                    }
                            }.also { activeObserver ->
                                conceptTitles.forEach(activeObserver::observe)
                            },
                        )
                    }
                }
            }.getOrElse {
                document.documentElement?.classList?.remove("motion-ready")
                revealElements.forEach { it.classList.add("is-revealed") }
                emptyList()
            }
        }

        onDispose {
            observers.forEach(IntersectionObserver::disconnect)
            document.documentElement?.classList?.remove("motion-ready")
        }
    }
}

@Composable
internal fun Sponsors(showCompanyNames: Boolean = true) {
    val sectionStyle = if (showCompanyNames) SponsorsStyle else HomeSponsorsStyle
    val contentStyle = if (showCompanyNames) SponsorsContentStyle else HomeSponsorsContentStyle
    Column(sectionStyle.toModifier().attr("id", "sponsors"), horizontalAlignment = Alignment.CenterHorizontally) {
        Column(ContentContainerStyle.toModifier().then(contentStyle.toModifier())) {
            SpanText(
                "Kotlin Festは、協賛企業の皆様のご支援により開催しています。",
                SponsorIntroStyle.toModifier(),
            )
            sponsorPlans.forEach { SponsorPlanBlock(it, showCompanyNames) }
        }
    }
}

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home", fullWidth = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    RevealOnScrollEffect()
    Column(Modifier.fillMaxWidth()) {
        Hero()
        Concept()
        Overview()
        Sponsors(showCompanyNames = false)
    }
}
